import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toProperCase, toSentenceCase } from '../utils/textFormat';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { trackDemoEvent } from '../components/DemoTracker';

import ClaimSubmittedModal from '../components/ClaimSubmittedModal';
import NeededDocumentsModal from '../components/NeededDocumentsModal';
import UpgradeLimitPrompt from '../components/UpgradeLimitPrompt';
import { SelectOrgPrompt, useNeedsOrg } from '../components/SelectOrgPrompt';

import CompanyStep from '../components/intake/steps/CompanyStep';
import DateStep from '../components/intake/steps/DateStep';
import LossTypeStep from '../components/intake/steps/LossTypeStep';
import DriverStep from '../components/intake/steps/DriverStep';
import EquipmentStep from '../components/intake/steps/EquipmentStep';
import LocationStep from '../components/intake/steps/LocationStep';
import DocumentsStep from '../components/intake/steps/DocumentsStep';

import { INITIAL_FORM_DATA, PROPER_FIELDS, SENTENCE_FIELDS, buildDocTypes } from '../components/intake/intakeConstants';
import { validateStep } from '../components/intake/intakeValidation';
import { buildClaimPayload } from '../components/intake/intakeSubmit';

import { API_BASE as API } from '../config';

const TOTAL_STEPS = 7;

/**
 * Multi-step Claims Intake Form orchestrator.
 * Owns form state, navigation, validation, submit + file upload, and the
 * post-submit confirmation flow. Each step UI lives in components/intake/steps/.
 */
export default function IntakeForm() {
  const navigate = useNavigate();
  const { operatorContext, userOrgs: _userOrgs, activeOrgId } = useAuth();
  const needsOrg = useNeedsOrg();
  const isOperatorInContext = operatorContext?.tenantId;

  const [companies, setCompanies] = useState([]);
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  const [showNeededDocsModal, setShowNeededDocsModal] = useState(false);
  const [limitPrompt, setLimitPrompt] = useState({ open: false, message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [claimId, setClaimId] = useState('');
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [previewIdx, setPreviewIdx] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  useEffect(() => {
    const params = activeOrgId ? `?org_id=${activeOrgId}` : '';
    axios.get(`${API}/companies${params}`).then((res) => setCompanies(res.data)).catch(console.error);
  }, [activeOrgId]);

  // ── Field-update helpers ────────────────────────────────────────────────
  const handleChange = (field, value) => {
    let v = value;
    if (typeof v === 'string') {
      if (PROPER_FIELDS.includes(field)) v = toProperCase(v);
      else if (SENTENCE_FIELDS.includes(field)) v = toSentenceCase(v);
    }
    setFormData((prev) => ({ ...prev, [field]: v }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData((prev) => ({ ...prev, [field]: checked }));
  };

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: '' }));

  const handleLossTypeToggle = (type) => {
    setFormData((prev) => ({
      ...prev,
      lossTypes: prev.lossTypes.includes(type)
        ? prev.lossTypes.filter((t) => t !== type)
        : [...prev.lossTypes, type],
    }));
  };

  const handleDocumentToggle = (doc) => {
    setFormData((prev) => ({
      ...prev,
      selectedDocuments: prev.selectedDocuments.includes(doc)
        ? prev.selectedDocuments.filter((d) => d !== doc)
        : [...prev.selectedDocuments, doc],
    }));
  };

  // ── File upload handlers ────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    setFormData((prev) => {
      const updated = [...prev.uploadedFiles, ...files];
      setPreviewIdx(prev.uploadedFiles.length);
      return { ...prev, uploadedFiles: updated };
    });
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setFormData((prev) => {
      const updated = [...prev.uploadedFiles, ...files];
      setPreviewIdx(prev.uploadedFiles.length);
      return { ...prev, uploadedFiles: updated };
    });
  };

  const removeFile = (idx) => {
    setFormData((prev) => {
      const updated = prev.uploadedFiles.filter((_, i) => i !== idx);
      if (previewIdx >= updated.length && previewIdx > 0) setPreviewIdx(updated.length - 1);
      return { ...prev, uploadedFiles: updated };
    });
  };

  const handleDragOver = (e) => e.preventDefault();

  // ── Navigation + submit ─────────────────────────────────────────────────
  const handleNext = () => {
    const newErrors = validateStep(step, formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Check claim limit before submitting
      try {
        const limitRes = await axios.get(`${API}/billing/check-limit/claim`);
        if (!limitRes.data.allowed) {
          setLimitPrompt({ open: true, message: limitRes.data.reason });
          return;
        }
      } catch (err) {
        console.warn('[IntakeForm] claim limit check failed (proceeding)', err);
      }

      const payload = buildClaimPayload(formData, companies, activeOrgId);
      const response = await axios.post(`${API}/claims`, payload);
      const reportNo = response.data.report_no;

      if (formData.uploadedFiles.length > 0) {
        const fd = new FormData();
        fd.append('report_no', reportNo);
        formData.uploadedFiles.forEach((file, i) => fd.append('file_' + i, file));
        await axios.post(`${API}/claim-docs/upload`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setClaimId(reportNo);
      setShowSubmittedModal(true);
      trackDemoEvent('claim.created', { report_no: reportNo });
    } catch (err) {
      console.error('Error submitting claim:', err);
      toast.error('Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Post-submit modals ──────────────────────────────────────────────────
  const handleFinish = () => {
    setShowSubmittedModal(false);
    const docTypes = buildDocTypes(formData.otherVehicle);
    const uncheckedDocs = docTypes.filter((d) => !formData.selectedDocuments.includes(d));
    if (uncheckedDocs.length > 0) {
      setShowNeededDocsModal(true);
    } else {
      navigate('/all-entries');
    }
  };

  const handleCloseNeededDocs = () => {
    setShowNeededDocsModal(false);
    navigate('/all-entries');
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (needsOrg && !isOperatorInContext) return <SelectOrgPrompt />;

  const stepProps = { formData, errors, handleChange };

  return (
    <>
      <ClaimSubmittedModal isOpen={showSubmittedModal} onFinish={handleFinish} claimId={claimId} />
      {showNeededDocsModal && (
        <NeededDocumentsModal
          isOpen={showNeededDocsModal}
          onClose={handleCloseNeededDocs}
          neededDocuments={buildDocTypes(formData.otherVehicle).filter(
            (d) => !formData.selectedDocuments.includes(d),
          )}
        />
      )}

      <div className="min-h-screen bg-gray-200 p-4 md:p-8" data-testid="intake-form">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl md:text-4xl font-bold text-blue-500 text-center mb-8 md:mb-12">
            Claims Intake Form
          </h1>

          <div className="flex flex-col items-center">
            <div className="w-full max-w-lg">
              {step === 1 && <CompanyStep {...stepProps} companies={companies} />}
              {step === 2 && <DateStep {...stepProps} />}
              {step === 3 && (
                <LossTypeStep
                  {...stepProps}
                  onToggleLossType={handleLossTypeToggle}
                  clearError={clearError}
                />
              )}
              {step === 4 && <DriverStep {...stepProps} />}
              {step === 5 && <EquipmentStep {...stepProps} />}
              {step === 6 && (
                <LocationStep
                  {...stepProps}
                  setFormData={setFormData}
                  setErrors={setErrors}
                  handleCheckboxChange={handleCheckboxChange}
                />
              )}
              {step === 7 && (
                <DocumentsStep
                  formData={formData}
                  errors={errors}
                  previewIdx={previewIdx}
                  setPreviewIdx={setPreviewIdx}
                  onFileUpload={handleFileUpload}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onRemoveFile={removeFile}
                  onToggleDocument={handleDocumentToggle}
                />
              )}

              <div className="flex justify-between mt-12">
                {step > 1 && (
                  <Button
                    onClick={handleBack}
                    className="bg-transparent hover:bg-blue-50 text-blue-500 border-none shadow-none px-0 text-xl"
                  >
                    &lt; Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={submitting}
                  data-testid="intake-next-btn"
                  className="bg-transparent hover:bg-blue-50 text-blue-500 border-none shadow-none px-0 text-xl ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {step === TOTAL_STEPS ? (submitting ? 'Submitting…' : 'Submit') : 'Next >'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UpgradeLimitPrompt
        isOpen={limitPrompt.open}
        onClose={() => setLimitPrompt({ open: false, message: '' })}
        message={limitPrompt.message}
      />
    </>
  );
}
